import apiClient from './client'
import type {
  LoginCredentials,
  RegisterData,
  AuthResponse,
  User,
  MagicLinkConsumeResponse,
  MagicLinkIssuePayload,
  ForgotPasswordPayload,
  ResetPasswordPayload,
  CompleteShadowAccountPayload,
  CompleteShadowAccountResponse,
  ChangePasswordPayload,
} from '@/types/auth'

export const authApi = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await apiClient.post('/auth/login', credentials)
    return response.data.data
  },

  async setContext(merchantId: string): Promise<{
    contextToken: string
    activeRole: string
    activeMerchant: { id: string; name: string; slug: string }
    activeBranch?: { id: string; name: string; code: string } | null
  }> {
    const response = await apiClient.post('/auth/set-context', { merchantId })
    return response.data.data
  },

  async register(userData: RegisterData): Promise<AuthResponse> {
    const response = await apiClient.post('/auth/register', userData)
    return response.data.data
  },

  async googleLogin(data: { idToken: string }): Promise<AuthResponse> {
    const response = await apiClient.post('/auth/google', data)
    return response.data.data
  },

  // Firebase token exchange: FE obtains Firebase ID token, BE verifies and issues platform JWT
  async firebaseLogin(data: { idToken: string }): Promise<AuthResponse> {
    const response = await apiClient.post('/auth/firebase', data)
    return response.data.data
  },

  async web3Login(data: { walletAddress: string; signature: string; message: string }): Promise<AuthResponse> {
    const response = await apiClient.post('/auth/web3', data)
    return response.data.data
  },

  async issueMagicLink(payload: MagicLinkIssuePayload) {
    const response = await apiClient.post('/auth/magic-link', payload)
    return response.data.data as {
      delivered: boolean
      expiresAt: string
      redirectPath: string | null
      magicLinkUrl?: string
    }
  },

  async consumeMagicLink(token: string): Promise<MagicLinkConsumeResponse> {
    const response = await apiClient.get('/auth/magic', {
      params: { token },
    })
    const body = response.data as { data?: MagicLinkConsumeResponse } & MagicLinkConsumeResponse
    return (body.data ?? body) as MagicLinkConsumeResponse
  },

  async getProfile(): Promise<User> {
    const response = await apiClient.get('/auth/profile')
    return response.data.data
  },

  async refreshToken(): Promise<{ access_token: string }> {
    const response = await apiClient.post('/auth/refresh')
    return response.data.data
  },

  async logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout')
    } catch (error) {
      // Ignore logout errors
    }
  },

  async forgotPassword(payload: ForgotPasswordPayload): Promise<void> {
    await apiClient.post('/auth/forgot-password', payload)
  },

  async resetPassword(payload: ResetPasswordPayload): Promise<void> {
    await apiClient.post('/auth/reset-password', payload)
  },

  async completeShadowAccount(payload: CompleteShadowAccountPayload): Promise<CompleteShadowAccountResponse> {
    const response = await apiClient.post('/auth/complete-shadow', payload)
    // Some BE endpoints return { data }, others return payload directly
    return (response.data?.data ?? response.data) as CompleteShadowAccountResponse
  },

  async changePassword(payload: ChangePasswordPayload): Promise<void> {
    await apiClient.post('/auth/change-password', payload)
  },
}




